import { DateTime } from 'luxon';
import { h } from 'vue';

import { renderBoolean, translateEnum } from '@rygen/rygen-ui/i18n';
import { ColumnSpecification } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import ClientAvatarAndName from '@/components/client/ClientAvatarAndName.vue';
import ManifestStatusChip from '@/components/manifest/ManifestStatusChip.vue';

import { Manifest } from '../shipment/manifest/Manifest';

import { ManifestColumnProps } from './ManifestColumnProps';

/**
 * Returns `'today'` or `'tomorrow'` when the manifest's calendar day is the
 * viewer's current or next calendar day, otherwise `undefined`. Used to
 * highlight imminent manifests in the list.
 */
const manifestDateHighlight = (
  data: Manifest,
): 'today' | 'tomorrow' | undefined => {
  const day = data.manifestDay;
  if (!day) {
    return undefined;
  }
  const diffDays = Math.round(
    day.diff(DateTime.now().startOf('day'), 'days').days,
  );
  if (diffDays === 0) {
    return 'today';
  }
  if (diffDays === 1) {
    return 'tomorrow';
  }
  return undefined;
};

const MANIFEST_NUMBER_COLUMN = new ColumnSpecification({
  field: 'manifestNumber',
  header: i18n.global.t('manifests.manifestNumber'),
  sortable: true,
  body: ({ data }: ManifestColumnProps) => data.manifestNumber ?? '-',
});

const CLIENT_COLUMN = new ColumnSpecification({
  field: 'client',
  header: i18n.global.t('clients.clients'),
  body: ({ data }: ManifestColumnProps) =>
    h(ClientAvatarAndName, {
      name: data.client?.name,
      logo: data.client?.branding?.avatar?.signedUrl,
      accentColor: data.client?.branding?.accentColor,
    }),
});

const CARRIER_COLUMN = new ColumnSpecification({
  field: 'carrier',
  header: i18n.global.t('carriers.carriers'),
  body: ({ data }: ManifestColumnProps) => {
    const name = data.carrier?.name ?? data.carrier?.scac ?? '-';
    if (!data.carrier?.scac || data.carrier.scac === data.carrier.name) {
      return name;
    }
    return h('div', {}, [
      h('div', {}, name),
      h('div', { class: 'text-gray-darker' }, data.carrier.scac),
    ]);
  },
});

const LOCATION_COLUMN = new ColumnSpecification({
  field: 'location',
  header: i18n.global.t('locations.locations'),
  body: ({ data }: ManifestColumnProps) => {
    const location = data.pickupLocation;
    const name = location?.locationName ?? location?.code ?? '-';
    const parts = [location?.city, location?.state].filter(Boolean).join(', ');
    return h('div', {}, [
      h('div', {}, name),
      parts ? h('div', { class: 'text-gray-darker' }, parts) : null,
    ]);
  },
});

const MANIFEST_DATE_COLUMN = new ColumnSpecification({
  field: 'manifestDate',
  header: i18n.global.t('common.paramDates', {
    param: i18n.global.t('manifests.manifests'),
    count: 1,
  }),
  sortable: true,
  body: ({ data }: ManifestColumnProps) => {
    const day = data.manifestDay;
    if (!day) {
      return '-';
    }
    const text = i18n.global.d(day.toJSDate(), 'numeric');
    const highlight = manifestDateHighlight(data);
    if (!highlight) {
      return text;
    }
    return h('span', { class: `manifest-date manifest-date--${highlight}` }, [
      `${text} `,
      h(
        'span',
        { class: 'manifest-date__badge' },
        i18n.global.t(`manifests.${highlight}`),
      ),
    ]);
  },
});

const SOURCE_COLUMN = new ColumnSpecification({
  field: 'source',
  header: i18n.global.t('common.source'),
  sortable: true,
  body: ({ data }: ManifestColumnProps) =>
    data.source ? translateEnum(data.source, 'ManifestSource') : '-',
});

const STATUS_COLUMN = new ColumnSpecification({
  field: 'status',
  header: i18n.global.t('common.status'),
  sortable: true,
  body: ({ data }: ManifestColumnProps) =>
    data.status ? h(ManifestStatusChip, { status: data.status }) : '-',
});

const SHIPMENT_COUNT_COLUMN = new ColumnSpecification({
  field: 'shipmentCount',
  header: i18n.global.t('shipments.shipments', 2),
  body: ({ data }: ManifestColumnProps) => `${data.shipmentCount}`,
});

const DOCUMENT_COLUMN = new ColumnSpecification({
  field: 'hasDocument',
  header: i18n.global.t('documents.documents', 2),
  body: ({ data }: ManifestColumnProps) => renderBoolean(data.hasDocument),
});

/**
 * All possible manifest columns.
 *
 * Order is significant. This array is used as the default set of columns.
 */
export const MANIFEST_COLUMNS: ColumnSpecification[] = [
  MANIFEST_NUMBER_COLUMN,
  CLIENT_COLUMN,
  CARRIER_COLUMN,
  LOCATION_COLUMN,
  MANIFEST_DATE_COLUMN,
  STATUS_COLUMN,
  SOURCE_COLUMN,
  SHIPMENT_COUNT_COLUMN,
  DOCUMENT_COLUMN,
];

export const MANIFEST_COLUMNS_SORT_FIELDS = MANIFEST_COLUMNS.map(
  (col) => col.field,
)
  .filter(notNull)
  .concat('id', '');
